import { useTranslation } from 'react-i18next';
import { Loading } from '@shape-ds/components';
import { useState, useEffect } from 'react';
import { DateFilters, InfoMessage } from '@shape/lighthouse-components';
import { RadarViewChart } from 'components/RadarViewChart/RadarViewChart';
import { Dayjs, dayjs } from 'lib';
import useFiltersLocalStorage from 'components/LineCharts/hooks/useFiltersLocalStorage';
import { useRadarViewData } from '../../hooks/useRadarViewData';
import { changePeriod, useDatesHelper } from '../../helpers';

export type RadialScalingWrapperProps = {
  alarmDateRange?: { start: string; end: string };
  modelName: string;
  hasExpandIcon?: boolean;
  showTitle?: boolean;
};

const DEFAULT_PERIOD = '720';

export const RadialScalingWrapper = ({
  modelName,
  alarmDateRange,
  hasExpandIcon = false,
  showTitle = true,
}: RadialScalingWrapperProps) => {
  const { t } = useTranslation();
  const { buildUTCDateRange } = useDatesHelper();
  const CHART_ID = `event-radar-${modelName}`;
  const { saveOnLocalStorage, getSavedFilters } = useFiltersLocalStorage(CHART_ID);
  const localFilters = getSavedFilters(CHART_ID);
  const [period, setPeriod] = useState<string>(localFilters.period ?? DEFAULT_PERIOD);
  const periodOptions = ['30', '90', '180'].map((option) => ({
    label: t('components:date_filter.last_n_days', { days: option }),
    value: (+option * 24).toString(),
  }));

  const defaultDateRange =
    localFilters.period === 'custom'
      ? { start: dayjs(localFilters.dateRange?.start), end: dayjs(localFilters.dateRange?.end) }
      : buildUTCDateRange(period);

  const [dateRange, setDateRange] = useState(
    alarmDateRange && period !== 'custom' ? changePeriod(alarmDateRange, period) : defaultDateRange,
  );

  const onChangePeriod = (
    value: string,
    dateRange?: {
      start: Dayjs;
      end: Dayjs;
    },
  ) => {
    saveOnLocalStorage(
      { period: value, dateRange: dateRange ?? buildUTCDateRange(value) },
      CHART_ID,
    );
    setPeriod(value);
    setDateRange(dateRange ?? buildUTCDateRange(value));
  };

  const { data, error, isLoading } = useRadarViewData({
    modelName,
    start: dateRange.start,
    end: dateRange.end,
  });

  useEffect(() => {
    onChangePeriod(DEFAULT_PERIOD, dateRange);
  }, [modelName]);

  if (isLoading) {
    return (
      <div className="scaling-error-container">
        <Loading />
      </div>
    );
  }

  if ((!isLoading && data.length === 0) || data === null || data === undefined) {
    return (
      <>
        {showTitle && <h2 className="box-title">{t('model_page:sections.radar')}</h2>}

        <DateFilters
          periodOptions={periodOptions}
          defaultPeriod={period}
          enableCalendar
          enableCustomPeriod
          handlePeriodChange={(period) => onChangePeriod(period)}
          data-testid="date-filters"
          handleCalendarChange={(dateRange) =>
            onChangePeriod('custom', {
              end: dayjs(dateRange.endDate),
              start: dayjs(dateRange.startDate),
            })
          }
          defaultDateRange={
            period === 'custom' && dateRange.start ? [dateRange.start, dateRange.end] : undefined
          }
        />
        <div className="scaling-error-container">
          <InfoMessage
            type="error"
            message={t('components:line_chart.error')}
            iconSize={24}
            columnSize={12}
            data-testid="no-data-message"
          />
        </div>
      </>
    );
  }

  return (
    <>
      {showTitle && <h2 className="box-title">{t('model_page:sections.radar')}</h2>}
      <DateFilters
        periodOptions={periodOptions}
        defaultPeriod={period}
        enableCalendar
        enableCustomPeriod
        handlePeriodChange={(period) => onChangePeriod(period)}
        data-testid="date-filters"
        handleCalendarChange={(dateRange) =>
          onChangePeriod('custom', {
            end: dayjs(dateRange.endDate),
            start: dayjs(dateRange.startDate),
          })
        }
        defaultDateRange={
          period === 'custom' && dateRange.start ? [dateRange.start, dateRange.end] : undefined
        }
      />
      <RadarViewChart
        error={error}
        isLoading={isLoading}
        data={data}
        hasExpandIcon={hasExpandIcon}
        dateRange={{ start: dayjs(dateRange.start), end: dayjs(dateRange.end) }}
        hasTitle={false}
        data-testid="radar-view-chart"
      />
    </>
  );
};
